import React from "react";
import "../styles/BlockRenderer.css";

// Функция для получения текста на нужном языке
const getTextByLanguage = (textObj, preferredLang = 'kz') => {
  if (!textObj || typeof textObj !== 'object') return textObj || '';
  
  // Приоритет: предпочитаемый язык -> kz -> ru -> en -> первый доступный
  return textObj[preferredLang] ||
         textObj.kz ||
         textObj.ru ||
         textObj.en ||
         Object.values(textObj)[0] ||
         '';
};

// Компонент для безопасного отображения HTML контента
const HTMLContent = ({ content, className = "", language = 'kz' }) => {
  const htmlContent = getTextByLanguage(content, language);
  
  if (!htmlContent) return null;
  
  return (
    <div 
      className={`html-content ${className}`}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

// Компоненты блоков с поддержкой HTML
const TitleBlock = ({ content, language = 'kz' }) => (
  <div className="title-block">
    <HTMLContent 
      content={content.title} 
      language={language} 
      className="title-main"
    />
    {content.subtitle && (
      <HTMLContent 
        content={content.subtitle} 
        language={language} 
        className="title-subtitle"
      />
    )}
  </div>
);

const TextBlock = ({ content, language = 'kz' }) => (
  <div className="text-block">
    <HTMLContent 
      content={content.text} 
      language={language} 
      className="text-content"
    />
  </div>
);

const ImageBlock = ({ content }) => (
  <div className="image-block">
    {content.images &&
      content.images.map((image, index) => (
        <div key={index} className="image-item">
          <img
            src={`${process.env.REACT_APP_API_URL}${image.imageUrl}`}
            alt={image.altText || `Image ${index + 1}`}
            loading="lazy"
          />
        </div>
      ))}
  </div>
);

const TextImageBlock = ({ content, language = 'kz' }) => (
  <div className={`text-image-block ${content.imagePosition || 'right'}`}>
    <div className="text-content">
      <HTMLContent 
        content={content.text} 
        language={language} 
        className="text-with-image"
      />
    </div>
    {content.imageUrl && (
      <div className="image-content">
        <img
          src={`${process.env.REACT_APP_API_URL}${content.imageUrl}`}
          alt={getTextByLanguage(content.altText, language) || "Image"}
          loading="lazy"
        />
      </div>
    )}
  </div>
);

const LinksBlock = ({ content, language = 'kz' }) => {
  if (content.displayStyle === "table") {
    return (
      <div className="links-block table-style">
        <HTMLContent 
          content={content.title} 
          language={language} 
          className="links-title"
        />
        <div className="table-container">
          <table className="links-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Ссылка</th>
              </tr>
            </thead>
            <tbody>
              {content.links &&
                content.links.map((link, index) => (
                  <tr
                    key={index}
                    onClick={() => window.open(getTextByLanguage(link.url, language), "_blank")}
                    className="clickable-row"
                  >
                    <td>{index + 1}</td>
                    <td>{getTextByLanguage(link.text, language)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (content.displayStyle === "cards") {
    return (
      <div className="links-block cards-style">
        <HTMLContent 
          content={content.title} 
          language={language} 
          className="links-title"
        />
        <div className="links-cards">
          {content.links &&
            content.links.map((link, index) => (
              <a
                key={index}
                href={getTextByLanguage(link.url, language)}
                target="_blank"
                rel="noopener noreferrer"
                className="link-card"
              >
                <span className="card-number">{index + 1}</span>
                <span className="card-text">{getTextByLanguage(link.text, language)}</span>
                <span className="card-arrow">→</span>
              </a>
            ))}
        </div>
      </div>
    );
  }

  if (content.displayStyle === "buttons") {
    return (
      <div className="links-block buttons-style">
        <HTMLContent 
          content={content.title} 
          language={language} 
          className="links-title"
        />
        <div className="links-buttons">
          {content.links &&
            content.links.map((link, index) => (
              <a
                key={index}
                href={getTextByLanguage(link.url, language)}
                target="_blank"
                rel="noopener noreferrer"
                className="link-button"
              >
                {getTextByLanguage(link.text, language)}
              </a>
            ))}
        </div>
      </div>
    );
  }

  // Обычный стиль (по умолчанию)
  return (
    <div className="links-block default-style">
      <HTMLContent 
        content={content.title} 
        language={language} 
        className="links-title"
      />
      <ul className="links-list">
        {content.links &&
          content.links.map((link, index) => (
            <li key={index} className="link-item">
              <a href={getTextByLanguage(link.url, language)} target="_blank" rel="noopener noreferrer">
                <span className="link-text">{getTextByLanguage(link.text, language)}</span>
                <span className="link-icon">↗</span>
              </a>
            </li>
          ))}
      </ul>
    </div>
  );
};

// Рендер блока по его типу
const BlockRenderer = ({ block, language = 'kz' }) => {
  if (!block || !block.content) {
    return <div className="block-error">Ошибка: неверные данные блока</div>;
  }

  switch (block.type) {
    case "TitleBlock":
      return <TitleBlock content={block.content} language={language} />;
    case "TextBlock":
      return <TextBlock content={block.content} language={language} />;
    case "ImageBlock":
      return <ImageBlock content={block.content} />;
    case "TextImageBlock":
      return <TextImageBlock content={block.content} language={language} />;
    case "LinksBlock":
      return <LinksBlock content={block.content} language={language} />;
    default:
      return (
        <div className="block-error">Неизвестный тип блока: {block.type}</div>
      );
  }
};

export default BlockRenderer;